import styled from "styled-components";
import Select from "react-select";

// =====================
// Styled Components
// =====================
export const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

export const Topbar = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-bottom: 1px solid var(--border);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--brand) 0%, var(--brand-2) 50%, var(--accent) 100%);
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
    flex-direction: column;
    gap: 12px;
  }
`;

export const TopbarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const TopbarLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  
  @media (max-width: 768px) {
    justify-content: center;
    width: 100%;
  }
`;

export const BrandContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const SystemInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    align-items: center;
    text-align: center;
  }
`;

export const SystemTitle = styled.h1`
  font-family: 'Almarai', sans-serif;
  font-size: 1.5rem;
  font-weight: 800;
  color: var(--brand);
  margin: 0;
  line-height: 1.2;
  letter-spacing: -0.02em;
  
  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

export const SystemSubtitle = styled.p`
  font-family: 'Almarai', sans-serif;
  font-size: 0.9rem;
  color: var(--muted);
  margin: 2px 0 0 0;
  font-weight: 400;
  line-height: 1.4;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

export const QuickActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

export const QuickActionButton = styled.button`
  font-family: 'Almarai', sans-serif;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  background: transparent;
  border: 1px solid var(--border);
  border-radius: 6px;
  color: var(--muted);
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--bg);
    border-color: var(--brand);
    color: var(--brand);
  }
  
  &:before {
    content: '📞';
    font-size: 0.9rem;
  }
`;

export const StatusIndicator = styled.div`
  font-family: 'Almarai', sans-serif;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 20px;
  font-size: 0.8rem;
  color: #065f46;
  font-weight: 600;
  
  &:before {
    content: '🟢';
    font-size: 0.7rem;
  }
  
  @media (max-width: 768px) {
    display: none;
  }
`;

export const Logo = styled.img`
  width: 80px;
  height: auto;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  transition: transform 0.2s ease;
  
  &:hover {
    transform: scale(1.02);
  }
  
  @media (max-width: 768px) {
    width: 60px;
  }
`;

export const HeaderBar = styled.div`
  background: linear-gradient(135deg, var(--brand) 0%, var(--brand-2) 100%);
  padding: 16px 24px;
  color: #ffffff;
  position: relative;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255,255,255,0.1), transparent);
    animation: shimmer 3s infinite;
  }
  
  @keyframes shimmer {
    0% { left: -100%; }
    100% { left: 100%; }
  }
  
  @media (max-width: 768px) {
    padding: 12px 16px;
  }
`;

export const HeaderText = styled.div`
  font-family: 'Almarai', sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: 1.3rem;
  font-weight: 800;
  text-align: center;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
  letter-spacing: -0.01em;
  
  &:before {
    content: '📋';
    font-size: 1.5rem;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.2));
  }
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    
    &:before {
      font-size: 1.3rem;
    }
  }
`;

export const Main = styled.main`
  flex: 1;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  padding: 24px 16px;
  
  @media (max-width: 768px) {
    padding: 16px 12px;
  }
`;

export const Navigation = styled.nav`
  background: transparent;
  margin-bottom: 16px;
`;

export const NavList = styled.ul`
  display: flex;
  list-style: none;
  padding: 0;
  margin: 0;
  border-bottom: 1px solid var(--border);
`;

export const NavItem = styled.li`
  margin: 0;
`;

export const NavLink = styled.a`
  font-family: 'Almarai', sans-serif;
  display: block;
  padding: 12px 16px;
  color: ${props => props.active ? 'var(--brand)' : 'var(--muted)'};
  font-weight: 700;
  text-decoration: none;
  border-bottom: 2px solid ${props => props.active ? 'var(--brand)' : 'transparent'};
  cursor: pointer;
  transition: color 0.2s ease, border-color 0.2s ease;
  letter-spacing: -0.01em;
  
  &:hover {
    color: var(--brand);
  }
`;

export const Section = styled.section`
  margin: 24px 0;
  position: relative;
  
  &:has(.service-overlay) {
    pointer-events: none;
    
    > *:not(.service-overlay) {
      filter: blur(1px);
      opacity: 0.6;
    }
  }
`;

export const SectionTitle = styled.h2`
  font-family: 'Almarai', sans-serif;
  font-size: 1.5rem;
  margin: 0 0 24px;
  color: var(--brand);
  text-align: center;
  font-weight: 800;
  position: relative;
  letter-spacing: -0.02em;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: var(--brand-2);
    margin: 12px auto 0;
    border-radius: 2px;
  }
`;

export const Card = styled.div`
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 8px 32px rgba(16, 24, 40, 0.08);
  
  @media (max-width: 768px) {
    padding: 24px 20px;
    border-radius: 12px;
  }
`;

export const NoteCard = styled(Card)`
  font-family: 'Almarai', sans-serif;
  background: #f0f6ff;
  border-color: #dbeafe;
  position: relative;
  font-weight: 400;
  line-height: 1.5;
  margin-bottom: 10px;
  
  &.secondary {
    background: #fff7ed;
    border-color: #fdba74;
  }
  
  &.conditional {
    background: #fef3c7;
    border-color: #f59e0b;
    border-left: 4px solid #f59e0b;
  }
`;

export const ConditionalAlert = styled.div`
  font-family: 'Almarai', sans-serif;
  background: #fef3c7;
  border: 1px solid #f59e0b;
  border-left: 4px solid #f59e0b;
  border-radius: 8px;
  padding: 16px;
  margin: 12px 0;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  
  &:before {
    content: '⚠️';
    font-size: 1.2rem;
    flex-shrink: 0;
  }
  
  p {
    font-family: 'Almarai', sans-serif;
    margin: 0;
    color: #92400e;
    line-height: 1.5;
    font-weight: 400;
  }
  
  a {
    font-family: 'Almarai', sans-serif;
    color: #92400e;
    font-weight: 600;
    text-decoration: underline;
  }
`;

export const FormGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

export const FormSection = styled.div`
  background: var(--card);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 24px;
`;

export const SectionHeader = styled.h3`
  font-family: 'Almarai', sans-serif;
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--brand);
  margin: 0 0 20px 0;
  padding-bottom: 12px;
  border-bottom: 2px solid var(--border);
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: -0.01em;
  
  &:before {
    content: '';
    width: 4px;
    height: 20px;
    background: var(--brand-2);
    border-radius: 2px;
  }
`;

export const FieldGroup = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 20px;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  width: ${(p) => p.fullWidth ? '100%' : 'auto'};
`;

export const Label = styled.label`
  font-family: 'Almarai', sans-serif;
  display: block;
  margin-bottom: 8px;
  color: var(--brand);
  font-weight: 700;
  font-size: 0.95rem;
  line-height: 1.4;
  
  &.required:after {
    content: ' *';
    color: var(--error);
  }
`;

export const Input = styled.input`
  font-family: 'Almarai', sans-serif;
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fcfcfd;
  color: var(--ink);
  font-size: 1rem;
  font-weight: 400;
  outline: none;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  
  &::placeholder {
    color: var(--muted);
    font-weight: 300;
  }
  
  &:hover {
    border-color: var(--brand);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  &:focus {
    border-color: var(--brand-2);
    box-shadow: 0 0 0 3px rgba(40, 123, 194, 0.15);
  }
  
  &[aria-invalid="true"] {
    border-color: var(--error);
    box-shadow: 0 0 0 3px rgba(210, 27, 76, 0.15);
  }
`;

export const Textarea = styled.textarea`
  font-family: 'Almarai', sans-serif;
  width: 100%;
  min-height: 120px;
  padding: 12px 16px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: #fcfcfd;
  color: var(--ink);
  font-size: 1rem;
  font-weight: 400;
  resize: vertical;
  outline: none;
  transition: all 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  line-height: 1.6;
  
  &::placeholder {
    color: var(--muted);
    font-weight: 300;
  }
  
  &:hover {
    border-color: var(--brand);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
  
  &:focus {
    border-color: var(--brand-2);
    box-shadow: 0 0 0 3px rgba(40, 123, 194, 0.15);
  }
  
  &[aria-invalid="true"] {
    border-color: var(--error);
    box-shadow: 0 0 0 3px rgba(210, 27, 76, 0.15);
  }
`;

export const Helper = styled.div`
  font-family: 'Almarai', sans-serif;
  margin-top: 6px;
  font-size: 0.8rem;
  color: var(--muted);
  line-height: 1.4;
  font-weight: 300;
`;

export const FieldHelper = styled.div`
  font-family: 'Almarai', sans-serif;
  margin-top: 4px;
  font-size: 0.8rem;
  color: var(--muted);
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 4px;
  font-weight: 300;
  
  &:before {
    content: 'ℹ️';
    font-size: 0.7rem;
  }
`;

export const Error = styled.div`
  font-family: 'Almarai', sans-serif;
  margin-top: 6px;
  font-size: 0.85rem;
  color: var(--error);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  line-height: 1.4;
  
  &:before {
    content: '⚠️';
    font-size: 0.8rem;
  }
`;

export const Info = styled.div`
  font-family: 'Almarai', sans-serif;
  margin-top: 6px;
  font-size: 13px;
  color: var(--brand);
  font-weight: 400;
  line-height: 1.4;
`;

export const Actions = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid var(--border);
`;

export const PrimaryButton = styled.button`
  font-family: 'Almarai', sans-serif;
  padding: 14px 32px;
  border: none;
  border-radius: 8px;
  background: var(--accent);
  color: #fff;
  font-weight: 700;
  font-size: 1.1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 160px;
  box-shadow: 0 2px 8px rgba(225, 82, 62, 0.3);
  letter-spacing: -0.01em;
  
  &:hover:not(:disabled) {
    background: #d1472f;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(225, 82, 62, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

export const FormError = styled.div`
  font-family: 'Almarai', sans-serif;
  color: var(--error);
  font-weight: 600;
  line-height: 1.4;
`;

export const Banner = styled.div`
  font-family: 'Almarai', sans-serif;
  background: ${(p) => (p.ok ? "#ecfdf5" : "#fff1f2")};
  border: 1px solid ${(p) => (p.ok ? "#a7f3d0" : "#ffe4e6")};
  color: ${(p) => (p.ok ? "#065f46" : "#9f1239")};
  padding: 16px;
  border-radius: 12px;
  margin: 10px 0 18px;
  font-weight: 400;
  line-height: 1.5;
`;

export const ProcessSuccessContainer = styled.div`
  font-family: 'Almarai', sans-serif;
  text-align: center;
  background: linear-gradient(135deg, var(--ok) 0%, #22c55e 100%);
  color: #fff;
  padding: 40px 32px;
  border-radius: 16px;
  font-weight: 800;
  font-size: 1.2rem;
  box-shadow: 0 8px 32px rgba(43, 157, 116, 0.3);
  position: relative;
  letter-spacing: -0.01em;
  
  &:before {
    content: '✅';
    font-size: 3rem;
    display: block;
    margin-bottom: 16px;
  }
`;

export const ConfirmationCard = styled(Card)`
  font-family: 'Almarai', sans-serif;
  background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
  border: 2px solid var(--brand-2);
  text-align: center;
  
  h2 {
    font-family: 'Almarai', sans-serif;
    color: var(--brand);
    margin-bottom: 16px;
    font-weight: 700;
  }
  
  .reference-number {
    font-family: 'Almarai', sans-serif;
    background: var(--brand);
    color: white;
    padding: 12px 24px;
    border-radius: 8px;
    font-size: 1.3rem;
    font-weight: 800;
    margin: 16px 0;
    display: inline-block;
    letter-spacing: -0.01em;
  }
  
  .summary {
    font-family: 'Almarai', sans-serif;
    background: white;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 20px;
    margin-top: 20px;
    text-align: right;
    
    h4 {
      font-family: 'Almarai', sans-serif;
      color: var(--brand);
      margin-bottom: 12px;
      border-bottom: 1px solid var(--border);
      padding-bottom: 8px;
      font-weight: 700;
    }
    
    .summary-item {
      display: flex;
      justify-content: space-between;
      padding: 8px 0;
      border-bottom: 1px solid #f3f4f6;
      
      &:last-child {
        border-bottom: none;
      }
      
      .label {
        font-family: 'Almarai', sans-serif;
        font-weight: 600;
        color: var(--muted);
      }
      
      .value {
        font-family: 'Almarai', sans-serif;
        color: var(--ink);
        font-weight: 400;
      }
    }
  }
  
  p {
    font-family: 'Almarai', sans-serif;
    font-weight: 400;
    line-height: 1.5;
  }
`;

export const ProcessFailedContainer = styled.div`
  font-family: 'Almarai', sans-serif;
  text-align: center;
  background: var(--danger);
  color: #fff;
  padding: 32px;
  border-radius: 12px;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

export const Footer = styled.footer`
  font-family: 'Almarai', sans-serif;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-top: 1px solid var(--border);
  background: #fff;
  flex-wrap: wrap;
  gap: 12px;
  font-weight: 400;
  color: var(--muted);
`;

export const Inquiries = styled.div`
  font-family: 'Almarai', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  h5 {
    font-family: 'Almarai', sans-serif;
    font-weight: 700;
    margin: 0 0 8px 0;
    color: var(--brand);
  }
  
  a {
    font-family: 'Almarai', sans-serif;
    color: var(--brand);
    text-decoration: none;
    font-weight: 400;
    transition: color 0.2s ease;
    
    &:hover {
      color: var(--brand-2);
    }
  }
`;

export const SelectStyled = (props) => (
  <Select
    styles={{
      control: (base, state) => ({
        ...base,
        fontFamily: "'Almarai', sans-serif",
        borderRadius: 8,
        borderColor: state.isFocused ? "var(--brand-2)" : "var(--border)",
        boxShadow: state.isFocused ? "0 0 0 3px rgba(40,123,194,.15)" : "0 1px 3px rgba(0,0,0,0.1)",
        paddingInline: 4,
        minHeight: 48,
        fontSize: '1rem',
        fontWeight: 400,
        transition: 'all 0.2s ease',
        ":hover": {
          borderColor: state.isFocused ? "var(--brand-2)" : "var(--brand)",
          boxShadow: state.isFocused ? "0 0 0 3px rgba(40,123,194,.15)" : "0 2px 4px rgba(0,0,0,0.1)",
        },
      }),
      placeholder: (base) => ({
        ...base,
        fontFamily: "'Almarai', sans-serif",
        color: "var(--muted)",
        fontSize: '1rem',
        fontWeight: 300,
      }),
      singleValue: (base) => ({
        ...base,
        fontFamily: "'Almarai', sans-serif",
        fontWeight: 400,
      }),
      menu: (base) => ({ 
        ...base, 
        fontFamily: "'Almarai', sans-serif",
        zIndex: 10,
        borderRadius: 8,
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      }),
      option: (base, state) => ({
        ...base,
        fontFamily: "'Almarai', sans-serif",
        backgroundColor: state.isSelected ? 'var(--brand)' : state.isFocused ? 'var(--bg)' : 'white',
        color: state.isSelected ? 'white' : 'var(--ink)',
        fontSize: '1rem',
        fontWeight: 400,
      }),
    }}
    {...props}
  />
);

// Service Transfer Banner Overlay Components
export const ServiceOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.75);
  z-index: 999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 20px;
  backdrop-filter: blur(2px);
  animation: fadeIn 0.3s ease-in-out;
  pointer-events: all;
  min-height: 100%;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const ServiceBannerContainer = styled.div`
  background: rgba(255, 255, 255, 0.98);
  border-radius: 16px;
  padding: 0;
  max-width: 100%;
  width: 100%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  animation: slideDown 0.4s ease-out;
  border: 3px solid var(--accent);
  
  @keyframes slideDown {
    from {
      transform: translateY(-20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

export const ServiceBannerImage = styled.img`
  width: 100%;
  height: auto;
  display: block;
  max-height: none;
  object-fit: contain;
  border-radius: 16px;
  background: white;
`;

export const ServiceBannerContent = styled.div`
  padding: 20px 24px;
  text-align: center;
  background: white;
  
  @media (max-width: 768px) {
    padding: 16px 20px;
  }
`;

export const ServiceBannerTitle = styled.h2`
  font-family: 'Almarai', sans-serif;
  font-size: 1.4rem;
  font-weight: 800;
  color: var(--brand);
  margin: 0 0 12px 0;
  line-height: 1.3;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

export const ServiceBannerText = styled.p`
  font-family: 'Almarai', sans-serif;
  font-size: 1rem;
  font-weight: 400;
  color: var(--ink);
  line-height: 1.6;
  margin: 0 0 16px 0;
  
  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

export const ServiceBannerNote = styled.p`
  font-family: 'Almarai', sans-serif;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--accent);
  margin: 0;
  padding: 12px 16px;
  background: #fef2f2;
  border-radius: 6px;
  border: 1px solid #fecaca;
`;
